/* eslint-disable max-classes-per-file */

import {
  extendObservable,
  observable,
  action,
  reaction,
  autorun,
  computed,
  when,
} from "mobx";

/**
 * @class AttributeStore
 * Speichert und verwaltet den Zustand der einzelnen Attribute
 * Darunter Fallen: Wert, Sichtbarkeit, ReadOnly, Required, Virtuell, subAttributes(ggf), computed
 * Die meisten Werten werden doppelt gespeichert: Der Ausgangswert aus der Config (auf dem ggf zurückgesetzt wird) und dem ggf geänderten Wert (z.b. aus Attributabhängigkeiten)
 * Daraus wird der aktuelle Zustand berechnet.
 * Änderungen von außen dürfen nur über die (action) setter Erfolgen (Observalble)
 * Die einzelnen AttributeStores sind über den DataStore erreichbar
 */
class AttributeStore {
  constructor(attrConf, handler, dataStore, subAttributes) {
    const {
      value,
      visible = true,
      readOnly = false,
      required = false,
      virtuell = false,
      isComputed = false,
    } = attrConf;

    this.configSource = JSON.parse(JSON.stringify(attrConf));
    this.targetConfig = JSON.parse(JSON.stringify(this.configSource));
    extendObservable(
      this,
      {
        setAttributeOptions: (options)=>this.updateOptions = options, 
        updateOptions: undefined, 
        valueStart: value,
        valueChanged: undefined,
        visibleStart: visible,
        visibleChanged: undefined,
        readOnlyStart: readOnly,
        readOnlyChanged: undefined,
        requiredStart: required,
        requiredChanged: undefined,
        virtuell,
        subAttributes,
        isComputed,
        computedVal: undefined,
        modul: undefined,
        get visible() {
          if (typeof this.visibleChanged !== "undefined")
            return this.visibleChanged;
          return this.visibleStart;
        },
        get value() {
          if (typeof this.subAttributes !== "undefined") {
            return this.subAttributes.map(
              (id) => this.DataStore.attributeValues[id].value
            );
          }
          if (this.isComputed) {
            return this.computedVal;
          }
          if (typeof this.valueChanged !== "undefined")
            return this.valueChanged;
          return this.valueStart;
        },
        get readOnly() {
          if (typeof this.readOnlyChanged !== "undefined")
            return this.readOnlyChanged;
          return this.readOnlyStart;
        },
        get required() {
          if (typeof this.requiredChanged !== "undefined")
            return this.requiredChanged;
          return this.requiredStart;
        },
        get hasError() {
          if (!this.modul) return false;
          return this.modul.hasError();
        },
        setValue: this.setValue,
        setVisible: this.setVisible,
        setRequired: this.setRequired,
        setReadOnly: this.setReadOnly,
        resetValue: this.resetValue,
        resetVisible: this.resetVisible,
        resetRequired: this.resetRequired,
        resetReadOnly: this.resetReadOnly,
        refreshComputedVal: this.refreshComputedVal,
        setModul: this.setModul,
        reset: this.reset,
      },
      {
        setAttributeOptions: action, 
        updateOptions: observable, 
        valueStart: observable,
        valueChanged: observable,
        visibleStart: observable,
        visibleChanged: observable,
        readOnlyStart: observable,
        readOnlyChanged: observable,
        requiredStart: observable,
        requiredChanged: observable,
        virtuell: observable,
        subAttributes: observable,
        isComputed: observable,
        computedVal: observable,
        visible: computed,
        value: computed,
        readOnly: computed,
        required: computed,
        hasError: computed,
        modul: observable,
        setValue: action,
        setVisible: action,
        setRequired: action,
        setReadOnly: action,
        resetValue: action,
        resetVisible: action,
        resetRequired: action,
        resetReadOnly: action,
        refreshComputedVal: action,
        setModul: action,
        reset: action,
      }
    );
    this.setHandler(handler);
    this.DataStore = dataStore;
    this.setUpComputedReaction();
    if (this.modul)
      this.DataStore.setUpReactionExternInfo(
        this.configSource,
        this.targetConfig,
        "$",
        this.modul
      );
  }

  reset(attrConf) {
    const {
      value,
      visible = true,
      readOnly = false,
      required = false,
      virtuell = false,
      isComputed = false,
    } = attrConf;
    this.valueStart = value;
    this.readOnlyStart = readOnly;
    this.requiredStart = required;
    this.visibleStart = visible;
    this.isComputed = isComputed;
    this.virtuellStart = virtuell;
  }

  setHandler(handler) {
    if (handler.messageHandler) this.messageHandler = handler.messageHandler;
    if (handler.dataHandler) this.dataHandler = handler.dataHandler;
    if (handler.requestHandler) this.requestHandler = handler.requestHandler;
    if (handler.dependencyHandler)
      this.dependencyHandler = handler.dependencyHandler;
    if (handler.saveChangesHandler)
      this.saveChangesHandler = handler.saveChangesHandler;
  }

  setUpComputedReaction() {
    if (this.isComputed) {
      this.computedVal = this.value;
      this.isComputed.attributes.forEach((id) => {
        reaction(
          () => this.DataStore.attributeValues[id].value,
          () => this.refreshComputedVal()
        );
      });
      if (
        typeof this.isComputed.externAttributes !== typeof undefined &&
        typeof this.DataStore.externStore !== typeof undefined
      ) {
        this.isComputed.externAttributes.forEach((id) => {
          reaction(
            () => this.DataStore.externStore[id],
            () => this.refreshComputedVal()
          );
        });
      }
    }
  }

  refreshComputedVal() {
    const values = this.isComputed.attributes.map((id) =>
      typeof this.DataStore.attributeValues[id].value === typeof undefined &&
      typeof this.isComputed.neutral !== typeof undefined
        ? this.isComputed.neutral
        : this.DataStore.attributeValues[id].value
    );
    let extValues = [];
    if (
      typeof this.isComputed.externAttributes !== typeof undefined &&
      typeof this.DataStore.externStore !== typeof undefined
    ) {
      extValues = this.isComputed.externAttributes.map(
        (
          id // values.concat(
        ) =>
          typeof this.DataStore.externStore[id] === typeof undefined &&
          typeof this.isComputed.neutral !== typeof undefined
            ? this.isComputed.neutral
            : this.DataStore.externStore[id]
      ); // );
    }
    this.computedVal = this.isComputed.compFun(values, extValues);
  }

  setModul(modul) {
    this.modul = modul;
    this.DataStore.setUpReactionExternInfo(
      this.configSource,
      this.targetConfig,
      "$",
      this.modul
    );
  }

  setValue(value) {
    this.valueChanged = value;
    if (this.subAttributes !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          when(
            () =>
              this.DataStore.attributeValues[this.subAttributes[i]] !==
              undefined,
            () => {
              this.DataStore.attributeValues[this.subAttributes[i]].setValue(v);
            }
          );
        });
      } else if (value !== undefined) {
        this.subAttributes
          .filter((id) => value[id] !== undefined)
          .forEach((id) =>
            when(
              () => this.DataStore.attributeValues[id] !== undefined,
              () => {
                this.DataStore.attributeValues[id].setValue(value[id]);
              }
            )
          );
      }
    }
  }

  resetValue() {
    this.valueChanged = undefined;
  }

  setVisible(disp) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "setVisible", disp);
    if (this.visible !== disp) {
      if (this.visibleChanged !== undefined) {
        messageHandler.logDebug("conflict");
      }
      this.visibleChanged = disp;
    } else {
      messageHandler.logDebug("nothing to set");
    }
  }

  resetVisible(disp) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "resetVisible", disp);
    if (this.visible === disp) {
      this.visibleChanged = undefined;
    } else {
      messageHandler.logDebug("nothing to reset");
    }
  }

  setReadOnly(ro) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "setReadonly", ro);
    if (this.readOnly !== ro) {
      if (this.readOnlyChanged !== undefined) {
        messageHandler.logDebug("conflict");
      }
      this.readOnlyChanged = ro;
    } else {
      messageHandler.logDebug("nothing to set");
    }
  }

  resetReadOnly(ro) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "resetReadOnly", ro);
    if (this.readOnly === ro) {
      this.readOnlyChanged = undefined;
    } else {
      messageHandler.logDebug("nothing to reset");
    }
  }

  setRequired(req) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "setRequired", req);
    if (this.required !== req) {
      if (this.requiredChanged !== undefined) {
        messageHandler.logDebug("conflict");
      }
      this.requiredChanged = req;
    } else {
      messageHandler.logDebug("nothing to set");
    }
  }

  resetRequired(req) {
    const messageHandler = this.DataStore.getHandler().messageHandler;
    messageHandler.logDebugFormatted("AttributeModul", "resetRequired", req);
    if (this.required === req) {
      this.requiredChanged = undefined;
    } else {
      messageHandler.logDebug("nothing to reset");
    }
  }
}
export default AttributeStore;
